import type { PropsWithChildren, FunctionComponent } from 'react'
import { useCallback } from 'react'
import styled from '@emotion/styled'
import { colors, spacing, LoadingDots, borderRadius } from '@retailer-platform/shared-components'
import { Box } from '@instacart/ids-core'
import { Link, Text } from '@retailer-platform/shared-components/src/tds'
import { useDashMessages } from '../../../intl/intl.hooks'
import { useCreateHref } from '../../../utils/routing/navigation.hooks'

const Wrapper = styled.div({
  position: 'relative',
  display: 'flex',
  width: '100%',
  padding: `${spacing.X16} ${spacing.X20}`,
})

const Container = styled.div({
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  border: `thin solid ${colors.GRAYSCALE.X20}`,
  borderRadius: borderRadius.X12,
})

const HeaderContainer = styled.div({
  height: '50px',
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-between',
  margin: `${spacing.X24} ${spacing.X16} 0 ${spacing.X16}`,
})

const MetricsContainer = styled.div({
  display: 'flex',
  flexDirection: 'row',
})

const LoadingContainer = styled.div({
  margin: `${spacing.X24} auto`,
})

interface PickingPerformanceOverviewProps {
  title?: string
  subtitle?: string
  isLoading?: boolean
  showAnalyticsCta?: boolean
  onAnalyticsCtaClicked?: () => void
  warehouseLocationId?: number
  retailerLocationId?: string
}

export const PickingPerformanceOverview: FunctionComponent<
  PropsWithChildren<PickingPerformanceOverviewProps>
> = ({
  title = '',
  subtitle = '',
  isLoading = true,
  showAnalyticsCta,
  onAnalyticsCtaClicked,
  warehouseLocationId,
  retailerLocationId,
  children,
}) => {
  const messages = useDashMessages({
    analyticsCta: 'shiftsDomain.performance.cta.toPickupAnalytics',
  })

  const retailerLocationSelectValue = JSON.stringify({ retailerLocationId })
  const pickupAnalyticsUrl = useCreateHref('insightsPortal', {
    warehouseLocationId,
    retailerLocationSelectValue,
  })

  const handleClick = useCallback(() => {
    onAnalyticsCtaClicked?.()
  }, [onAnalyticsCtaClicked])

  const header = (
    <HeaderContainer>
      <div>
        <Text typography="title">{title}</Text>
        <Text typography="bodyRegular">{subtitle}</Text>
      </div>
      {showAnalyticsCta && (
        <Box css={{ height: '100%' }}>
          <Link
            href={pickupAnalyticsUrl}
            onPress={() => {
              handleClick()
            }}
          >
            {messages.analyticsCta}
          </Link>
        </Box>
      )}
    </HeaderContainer>
  )

  if (isLoading) {
    return (
      <Wrapper>
        <Container>
          {header}
          <MetricsContainer>
            <LoadingContainer>
              <LoadingDots height={20} centered testId="pickingPerformanceOverviewLoading" />
            </LoadingContainer>
          </MetricsContainer>
        </Container>
      </Wrapper>
    )
  }

  return (
    <Wrapper>
      <Container>
        {header}
        <MetricsContainer>{children}</MetricsContainer>
      </Container>
    </Wrapper>
  )
}
